
import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Link2 } from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';
import { useUserRoles } from '@/hooks/useUserRoles';
import { supabase } from '@/integrations/supabase/client';

interface IncidentLinkDialogProps {
  teamId?: string;
}

export const IncidentLinkDialog = ({ teamId }: IncidentLinkDialogProps) => {
  const { t } = useLanguage();
  const { isAdmin } = useUserRoles();
  const [reportLink, setReportLink] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (teamId && isOpen) {
      fetchReportLink();
    }
  }, [teamId, isOpen]);

  const fetchReportLink = async () => {
    if (!teamId) return;
    
    try {
      setIsLoading(true);
      
      // Try to get the link from Supabase first
      const { data, error } = await supabase
        .from('team_incident_links' as any)
        .select('link_url')
        .eq('team_id', teamId)
        .maybeSingle();
      
      if (error) throw error;
      
      if (data) {
        setReportLink((data as any).link_url);
      } else {
        // Fallback to localStorage for backward compatibility
        const savedLink = localStorage.getItem(`incident-report-link-${teamId}`);
        if (savedLink) {
          setReportLink(savedLink);
          
          // If found in localStorage but not in database, migrate it
          await supabase
            .from('team_incident_links' as any)
            .insert({
              team_id: teamId,
              link_url: savedLink,
            });
        }
      }
    } catch (error: any) {
      console.error('Error fetching incident report link:', error);
      toast.error(t("errorFetchingLink") || "Failed to load incident report link");
      
      // Still try localStorage as fallback
      const savedLink = localStorage.getItem(`incident-report-link-${teamId}`);
      if (savedLink) {
        setReportLink(savedLink);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleSaveLink = async () => {
    if (!reportLink.trim()) {
      toast.error(t("linkRequired") || "Please enter a valid link");
      return;
    }

    if (!teamId) {
      toast.error(t("teamRequired") || "Team ID is required");
      return;
    }

    // Validate URL format
    try {
      new URL(reportLink);
      
      setIsLoading(true);

      // Save link to Supabase
      const { error: upsertError } = await supabase
        .from('team_incident_links' as any)
        .upsert(
          {
            team_id: teamId,
            link_url: reportLink,
            updated_at: new Date().toISOString()
          },
          { onConflict: 'team_id' }
        );

      if (upsertError) throw upsertError;
      
      // Also save to localStorage for backward compatibility
      localStorage.setItem(`incident-report-link-${teamId}`, reportLink);
      
      toast.success(t("linkSaved") || "Incident report link saved successfully");
      setIsOpen(false);
    } catch (e: any) {
      console.error('Error saving incident report link:', e);
      
      if (e instanceof Error && e.message.includes('URL')) {
        toast.error(t("invalidLink") || "Please enter a valid URL");
      } else {
        toast.error(t("errorSavingLink") || "Failed to save incident report link");
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Only render the button if the user is an admin
  if (!isAdmin) {
    return null;
  }

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" className="flex items-center gap-2">
          <Link2 className="h-4 w-4" />
          {t("incidentReportLink") || "Incident Link"}
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{t("configureIncidentLink") || "Configure Incident Report Link"}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label htmlFor="report-link">{t("submissionLink") || "Submission Link"}</Label>
            <Input 
              id="report-link" 
              type="url" 
              placeholder="https://example.com/submit-report" 
              value={reportLink} 
              onChange={e => setReportLink(e.target.value)} 
              disabled={isLoading}
            />
            <p className="text-sm text-gray-500">
              {t("enterLinkDescription") || "Enter the link where users can submit incident reports"}
            </p>
          </div>
        </div>
        <DialogFooter>
          <Button onClick={handleSaveLink} disabled={isLoading}>
            {isLoading ? (t("saving") || "Saving...") : (t("saveLink") || "Save Link")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
